/**
 * «Распределение» — `GET /api/leads/distribution`, выдача/снятие, соответствия
 * учёток (`/api/bitrix/user-links`). Спека
 * `docs/specs/2026-09-24-lead-distribution-design.md`.
 */
import { apiFetch } from "@/lib/api";

export type ByUser = {
  bitrix_user_id: number;
  bitrix_name: string;
  crm_user_id: number | null;
  name: string;
  active: boolean | null;
  leads: number;
};

export type NoPhoneLead = {
  lead_id: number;
  title: string;
  phone: string;
  created: string;
  bitrix_user_id: number | null;
  name: string;
};

export type Assignee = { crm_user_id: number; bitrix_user_id: number; name: string };

/** Сотрудник КЦ на линии сегодня: `leads` — NEW на нём сейчас, `give` — сколько
 * выдать для выравнивания; без учётки Bitrix (`bitrix_user_id` null) — 0. */
export type OnLine = {
  crm_user_id: number;
  bitrix_user_id: number | null;
  name: string;
  leads: number;
  give: number;
  /** Сколько «повторных прозвонов» выдать до того же ровного уровня. */
  redial_give: number;
};

/** Лид «В работе», по которому давно не звонили; `last_call` null — звонков нет. */
export type RedialLead = {
  lead_id: number;
  created: string;
  last_call: string | null;
  bitrix_user_id: number | null;
  name: string;
};

export type Redial = { hours: number; days: number; per_day: number; leads: RedialLead[] };

export type Distribution = {
  status: string;
  last_sync: string | null;
  total: number;
  unassigned: number;
  assigned: number;
  by_user: ByUser[];
  no_phone: NoPhoneLead[];
  users: Assignee[];
  on_line: OnLine[];
  redial: Redial;
};

export type AssignResult = {
  requested: number;
  found: number;
  changed: number;
  errors: { lead_id: number; error: string }[];
};

export type LinkUser = {
  crm_user_id: number;
  name: string;
  bitrix_user_id: number | null;
  /** manual — поставлено здесь, crm — из sUser.b24UserID, null — связи нет. */
  source: "manual" | "crm" | null;
};

export type BitrixUserOption = { id: number; name: string; active: boolean; position: string };

export type Links = { users: LinkUser[]; bitrix_users: BitrixUserOption[] };

export const loadDistribution = () => apiFetch<Distribution>("/api/leads/distribution");

export const assignLeads = (count: number, crm_user_id: number) =>
  apiFetch<AssignResult>("/api/leads/distribution/assign", {
    method: "POST",
    body: JSON.stringify({ count, crm_user_id }),
  });

/** «Повторные прозвоны в работе» → «Новая» на сотрудника (`docs/specs/2026-09-30-lead-redial-design.md`). */
export const redialLeads = (count: number, crm_user_id: number) =>
  apiFetch<AssignResult>("/api/leads/distribution/redial", {
    method: "POST",
    body: JSON.stringify({ count, crm_user_id }),
  });

export const unassignLeads = (bitrix_user_id: number) =>
  apiFetch<AssignResult>("/api/leads/distribution/unassign", {
    method: "POST",
    body: JSON.stringify({ bitrix_user_id }),
  });

export const loadLinks = () => apiFetch<Links>("/api/bitrix/user-links");

export const setLink = (crm_user_id: number, bitrix_user_id: number | null) =>
  apiFetch<{ ok: boolean }>("/api/bitrix/user-links", {
    method: "PUT",
    body: JSON.stringify({ crm_user_id, bitrix_user_id }),
  });

export const resetLink = (crm_user_id: number) =>
  apiFetch<{ ok: boolean }>(`/api/bitrix/user-links/${crm_user_id}`, { method: "DELETE" });

// --- Сделки обзвона: docs/specs/2026-09-25-deal-distribution-design.md ------------

/** Сотрудник КЦ на линии: `held` — сделок на нём (CRM + наши свежие выдачи),
 * `give` — сколько добавит «Добрать» до `limit`. */
export type DealOnLine = {
  crm_user_id: number;
  name: string;
  held: number;
  in_call_list: number;
  give: number;
  /** Сколько добавит «Перезвон» до `limit`. */
  redial_give: number;
  /** Сколько добавит «Повтор» до `limit` — по стадиям «Будущего перезвона». */
  repeat_give: Record<string, number>;
};

/** Сделка на перезвон: сегодня не дозвонились, звонок > 1 ч назад, попыток < 4. */
export type RedialDeal = {
  id: number;
  type_id: number;
  type: string;
  callable_now: boolean;
  local_time: string;
  last_call_at: string;
};

/** «Будущий перезвон»: сделка за 30 дней; `last_call_at` null — не звонили,
 * `attempts` — исходящих за 30 дней. */
export type RepeatDeal = Omit<RedialDeal, "last_call_at"> & {
  stage_id: string;
  created_at: string;
  last_call_at: string | null;
  attempts: number;
  wait_at: string | null;
};

export type DealRepeat = {
  days: number;
  /** Попыток за неделю меньше этого числа. */
  attempts: number;
  /** Сделок без контактов в копии — «не звонили» про них не знаем, не берём. */
  unknown: number;
  stages: { id: string; name: string; count: number }[];
  deals: RepeatDeal[];
};

export type DealDistribution = {
  now: string;
  limit: number;
  /** Типы обзвона в порядке выдачи и сколько в каждом без ответственного. */
  queue: { type_id: number; name: string; count: number }[];
  unassigned: number;
  redial: RedialDeal[];
  repeat: DealRepeat;
  on_line: DealOnLine[];
};

export type DealAssignResult = {
  held?: number;
  requested: number;
  found: number;
  changed: number;
  skipped?: number;
  by_type?: Record<string, number>;
  errors: { deal_id: number; error: string }[];
};

export const loadDealDistribution = () => apiFetch<DealDistribution>("/api/deals/distribution");

export const assignDeals = (crm_user_id: number) =>
  apiFetch<DealAssignResult>("/api/deals/distribution/assign", {
    method: "POST",
    body: JSON.stringify({ crm_user_id }),
  });

export const redialDeals = (crm_user_id: number) =>
  apiFetch<DealAssignResult>("/api/deals/distribution/redial", {
    method: "POST",
    body: JSON.stringify({ crm_user_id }),
  });

/** «Будущий перезвон» стадии `stage` (3 «В работе», 5 «Думают») — до `limit`. */
export const repeatDeals = (crm_user_id: number, stage: string) =>
  apiFetch<DealAssignResult>("/api/deals/distribution/repeat", {
    method: "POST",
    body: JSON.stringify({ crm_user_id, stage }),
  });

export const unassignDeals = (crm_user_id: number) =>
  apiFetch<DealAssignResult>("/api/deals/distribution/unassign", {
    method: "POST",
    body: JSON.stringify({ crm_user_id }),
  });

/** Сколько на ком и кто сколько обзвонил — `docs/specs/2026-09-25-deal-distribution-stats-design.md`. */
export type DealStatsCell = { called: number; closed: number | null };
export type DealStatsToday = {
  crm_user_id: number;
  name: string;
  cells: Record<string, DealStatsCell>;
  called: number;
  closed: number | null;
  calls: number;
  talks: number;
};
export type DealStats = {
  date: string;
  types: { id: number; name: string }[];
  morning: string | null;
  end: { kind: "live" | "evening"; at: string } | null;
  assigned: {
    types: { type_id: number; name: string; total: number; assigned: number; free: number }[];
    total: { total: number; assigned: number; free: number };
    users: { crm_user_id: number; name: string; cells: Record<string, number>; total: number }[];
  } | null;
  today: { users: DealStatsToday[]; total: DealStatsToday };
};

export const loadDealStats = () => apiFetch<DealStats>("/api/deals/distribution/stats");
